import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Activity, Users, Filter, TrendingUp, Target, CheckCircle2, FileText } from 'lucide-react';
import { SuppressionNotice } from './SuppressionNotice';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const MetricCard = ({ icon: Icon, label, value, sub, isDarkMode }) => (
  <div className={`rounded-lg border p-5 ${isDarkMode ? 'bg-zinc-900 border-zinc-800' : 'bg-white border-zinc-200'}`}>
    <div className="flex items-center gap-3 mb-3">
      <div className={`w-9 h-9 rounded-md flex items-center justify-center ${isDarkMode ? 'bg-zinc-800' : 'bg-zinc-100'}`}>
        <Icon size={18} className="text-demoraes-green" />
      </div>
      <span className={`text-xs font-medium uppercase tracking-wider ${isDarkMode ? 'text-zinc-500' : 'text-zinc-500'}`}>{label}</span>
    </div>
    <div className={`text-2xl font-bold tracking-tight ${isDarkMode ? 'text-zinc-100' : 'text-zinc-900'}`}
      style={{ fontFamily: 'Manrope, sans-serif' }}>
      {value}
    </div>
    {sub && <p className={`text-xs mt-1 ${isDarkMode ? 'text-zinc-500' : 'text-zinc-500'}`}>{sub}</p>}
  </div>
);

// Rótulos das dimensões (alinhados ao realinhamento metodológico COPSOQ II)
const DIMENSION_LABELS = {
  demandas: 'Demandas de Trabalho',
  controle: 'Controle e Autonomia',
  apoio: 'Apoio Social e Comunicação',
  reconhecimento: 'Reconhecimento e Justiça',
  seguranca: 'Segurança no Emprego',
  desenvolvimento: 'Desenvolvimento e Significado',
  saude: 'Saúde e Bem-estar',
  ofensivos: 'Comportamentos Ofensivos',
};

const DimensionRow = ({ dimension, data, isDarkMode }) => {
  const score = data.average_score;
  // Dimensões de exposição usam taxa de prevalência (menor = melhor);
  // as demais usam a escala COPSOQ 0-100 (maior = melhor).
  const isPrevalence = !!data.prevalence;
  const pct = Math.min(Math.max(score, 0), 100);
  const barColor = isPrevalence
    ? (score === 0 ? 'bg-demoraes-green' : score < 5 ? 'bg-amber-500' : 'bg-red-500')
    : (score >= 75 ? 'bg-demoraes-green' : score >= 50 ? 'bg-amber-500' : 'bg-red-500');
  const label = DIMENSION_LABELS[dimension] || dimension;
  // Dimensões fora do IPTO 2.0: desfecho de saúde e exposição a comportamentos ofensivos
  const badge = isPrevalence ? '% expostos' : (data.in_ipto === false ? 'desfecho' : null);

  return (
    <div className={`flex items-center gap-4 py-3 border-b last:border-b-0 ${isDarkMode ? 'border-zinc-800' : 'border-zinc-100'}`}>
      <div className={`w-40 flex items-center gap-1.5 min-w-0 ${isDarkMode ? 'text-zinc-300' : 'text-zinc-700'}`}
        title={isPrevalence ? `${label} — % de trabalhadores expostos (fora do IPTO 2.0)` : label}>
        <span className="text-sm font-medium truncate">{label}</span>
        {badge && (
          <span className={`text-[9px] px-1 py-0.5 rounded whitespace-nowrap flex-shrink-0 ${
            isDarkMode ? 'bg-zinc-800 text-zinc-500' : 'bg-zinc-100 text-zinc-500'
          }`}>
            {badge}
          </span>
        )}
      </div>
      <div className="flex-1">
        <div className={`h-2 rounded-full overflow-hidden ${isDarkMode ? 'bg-zinc-800' : 'bg-zinc-100'}`}>
          <div className={`h-full rounded-full transition-all duration-700 ${barColor}`} style={{ width: `${pct}%` }} />
        </div>
      </div>
      <div className={`w-20 text-right text-sm font-bold tabular-nums ${isDarkMode ? 'text-zinc-200' : 'text-zinc-800'}`}>
        {isPrevalence ? `${score.toFixed(1)}%` : score.toFixed(1)}
      </div>
      <div className={`w-24 text-right text-xs tabular-nums ${isDarkMode ? 'text-zinc-500' : 'text-zinc-500'}`}>
        {isPrevalence && data.respondents != null
          ? `${data.exposed_count} de ${data.respondents} expostos`
          : `${data.total_responses} resp.`}
      </div>
    </div>
  );
};

const RoundProgressRow = ({ tag, isDarkMode }) => {
  const pct = Math.min(tag.percentage, 100);
  const barColor = tag.is_full ? 'bg-demoraes-green' : pct >= 60 ? 'bg-emerald-500' : pct >= 30 ? 'bg-amber-500' : 'bg-zinc-400';

  return (
    <div className={`py-3 border-b last:border-b-0 ${isDarkMode ? 'border-zinc-800' : 'border-zinc-100'}`}>
      <div className="flex items-center justify-between mb-1.5">
        <div className="flex items-center gap-2 min-w-0">
          <span className={`text-sm font-medium truncate ${isDarkMode ? 'text-zinc-300' : 'text-zinc-700'}`} title={tag.client_name}>
            {tag.client_name}
          </span>
          {tag.round_label && (
            <span className={`text-[10px] px-1.5 py-0.5 rounded whitespace-nowrap ${isDarkMode ? 'bg-zinc-800 text-zinc-400' : 'bg-zinc-100 text-zinc-500'}`}>
              {tag.round_label}
            </span>
          )}
          {tag.is_full && <CheckCircle2 size={14} className="text-demoraes-green flex-shrink-0" />}
          {tag.report_emitted && (
            <span className="flex items-center gap-1 text-[10px] text-demoraes-green whitespace-nowrap" title="Relatório já emitido">
              <FileText size={12} /> Emitido
            </span>
          )}
        </div>
        <span className={`text-xs font-bold tabular-nums whitespace-nowrap ${isDarkMode ? 'text-zinc-200' : 'text-zinc-800'}`}>
          {tag.current_responses}/{tag.employee_count} ({tag.percentage}%)
        </span>
      </div>
      <div className={`h-2 rounded-full overflow-hidden ${isDarkMode ? 'bg-zinc-800' : 'bg-zinc-100'}`}>
        <div className={`h-full rounded-full transition-all duration-700 ${barColor}`} style={{ width: `${pct}%` }} />
      </div>
      {tag.last_response_at && (
        <div className={`mt-1 text-[10px] tabular-nums ${isDarkMode ? 'text-zinc-500' : 'text-zinc-400'}`}
          title="Data/hora da última resposta recebida (acompanhamento interno — não consta no relatório)">
          Última resposta: {new Date(tag.last_response_at).toLocaleDateString('pt-BR')} às {new Date(tag.last_response_at).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}
        </div>
      )}
    </div>
  );
};

export const DashboardTab = ({ results, filters, isDarkMode }) => {
  const [tagStatus, setTagStatus] = useState([]);

  useEffect(() => {
    let cancelled = false;
    axios.get(`${API}/admin/tag-status`)
      .then(res => { if (!cancelled) setTagStatus(res.data.tags || []); })
      .catch(() => { /* seção opcional — falha silenciosa */ });
    return () => { cancelled = true; };
  }, []);

  if (!results) return null;

  if (results.total_sessions === 0) {
    return (
      <div className={`rounded-lg border p-12 text-center ${isDarkMode ? 'bg-zinc-900 border-zinc-800' : 'bg-white border-zinc-200'}`}>
        <Activity size={48} className={`mx-auto mb-4 ${isDarkMode ? 'text-zinc-700' : 'text-zinc-300'}`} />
        <h3 className={`text-lg font-semibold mb-2 ${isDarkMode ? 'text-zinc-300' : 'text-zinc-700'}`}>
          Nenhum dado encontrado
        </h3>
        <p className={`text-sm ${isDarkMode ? 'text-zinc-500' : 'text-zinc-500'}`}>
          Ajuste os filtros para visualizar os resultados.
        </p>
      </div>
    );
  }

  // LGPD: recorte segmentado abaixo do n mínimo não exibe estatísticas
  if (results.suppressed) {
    return (
      <SuppressionNotice
        totalSessions={results.total_sessions}
        minRespondents={results.min_segment_respondents}
        isDarkMode={isDarkMode}
      />
    );
  }

  const activeFilterCount = (filters.tag ? 1 : 0) +
    (filters.departments?.length || 0) +
    (filters.company_times?.length || 0);

  return (
    <div className="space-y-6">
      {/* Metric cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <MetricCard icon={Users} label="Respondentes" value={results.total_sessions} sub="Questionários concluídos" isDarkMode={isDarkMode} />
        <MetricCard icon={Filter} label="Filtros Ativos" value={activeFilterCount || 'Nenhum'} sub="Segmentação aplicada" isDarkMode={isDarkMode} />
        <MetricCard icon={TrendingUp} label="IPTO 2.0" value={results.ipto_score != null ? results.ipto_score.toFixed(1) : '—'} sub="Ambiente psicossocial (0–100)" isDarkMode={isDarkMode} />
      </div>

      {/* Round progress */}
      {tagStatus.length > 0 && (
        <div className={`rounded-lg border p-6 ${isDarkMode ? 'bg-zinc-900 border-zinc-800' : 'bg-white border-zinc-200'}`}>
          <div className="flex items-center justify-between mb-4">
            <h3 className={`text-base font-bold flex items-center gap-2 ${isDarkMode ? 'text-zinc-200' : 'text-zinc-800'}`}
              style={{ fontFamily: 'Manrope, sans-serif' }}>
              <Target size={16} className="text-demoraes-green" />
              Progresso da Rodada
            </h3>
            <span className={`text-xs ${isDarkMode ? 'text-zinc-500' : 'text-zinc-400'}`}>
              {tagStatus.filter(t => t.is_full).length}/{tagStatus.length} completas
            </span>
          </div>
          <div>
            {tagStatus.map((tag) => (
              <RoundProgressRow key={`${tag.tag}-${tag.round || ''}`} tag={tag} isDarkMode={isDarkMode} />
            ))}
          </div>
        </div>
      )}

      {/* Dimension results */}
      <div className={`rounded-lg border p-6 ${isDarkMode ? 'bg-zinc-900 border-zinc-800' : 'bg-white border-zinc-200'}`}>
        <h3 className={`text-base font-bold mb-1 ${isDarkMode ? 'text-zinc-200' : 'text-zinc-800'}`}
          style={{ fontFamily: 'Manrope, sans-serif' }}>
          Resultados por Dimensão
        </h3>
        <p className={`text-xs mb-4 ${isDarkMode ? 'text-zinc-500' : 'text-zinc-500'}`}>
          Escala COPSOQ 0–100: quanto maior, mais favorável. Comportamentos Ofensivos são reportados
          por prevalência (% de trabalhadores expostos), onde menor é melhor.
        </p>
        <div>
          {results.consolidated && Object.entries(results.consolidated).map(([dimension, data]) => (
            <DimensionRow key={dimension} dimension={dimension} data={data} isDarkMode={isDarkMode} />
          ))}
        </div>
      </div>
    </div>
  );
};
